import React, { useContext, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import BedRockContext from "../../BedRockContext";
import { PageTitle } from "../../../_metronic/layout/core";
import { GenericTable } from "../generic/table/GenericTable";
import { defaultList } from "../generic/InitialStates";
import { HeaderColumnFormatter } from "../../column-formatters/HeaderColumnFormatter";
import { ActionsColumnFormatter } from "../../column-formatters/ActionsColumnFormatter";
import { ENTITY_ID_RAW_PURCHASE, ENTITY_ID_SUPPLIER } from "../generic/Constants";
import { genericDelete } from "../generic/crud";
import { showConfirmDialog } from "../../generic-components/ConfirmDialog";
import { LoaderSlip } from "../sale/LoaderSlip";
import { PendingEntryEditDialog } from "../sale/PendingEntryEditDialog";
import { useAuth } from "../../modules/auth";
import { formatDateTime } from "../../utils/Utils";
import { QrPurchaseBadges } from "../qr-purchase/qrReasonLabels";
import { NOT_QR_DRAFT_FILTERS } from "../qr-purchase/qrDraftActions";

export function PendingPurchasesPage() {
    const myData = useContext(BedRockContext);
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [reload, setReload] = useState(1);
    const [pageLoadComplete, setPageLoadComplete] = useState(false);
    const [showLoaderSlip, setShowLoaderSlip] = useState(false);
    const [loaderSlipData, setLoaderSlipData] = useState(null);
    const [editRow, setEditRow] = useState(null);

    const profileBreadCrumbs = [
        {
            title: "Home",
            path: "/dashboard",
            isSeparator: false,
            isActive: false,
        },
        {
            title: "",
            path: "",
            isSeparator: true,
            isActive: false,
        },
        {
            title: "Purchases",
            path: "/purchases-list",
            isSeparator: false,
            isActive: false,
        },
        {
            title: "",
            path: "",
            isSeparator: true,
            isActive: false,
        },
    ];

    // Check if record is older than 48 hours
    function isOlderThan48Hours(row) {
        if (!row.purchaseFirstStepDate || row.purchaseType !== "TWO_STEP" || !row.isPartialRecord) return false;
        const entryDate = new Date(row.purchaseFirstStepDate);
        const hoursDiff = (Date.now() - entryDate.getTime()) / (1000 * 60 * 60);
        return hoursDiff > 48;
    }

    // Delete stale record
    async function deleteRecord(row) {
        const confirmed = await showConfirmDialog("Are you sure you want to delete this pending purchase?");
        if (!confirmed) return;
        genericDelete(ENTITY_ID_RAW_PURCHASE, row.id).then(() => {
            setReload(prev => prev + 1);
        });
    }

    // Complete purchase action
    function completePurchase(row) {
        navigate(`/purchase-new/edit/${row.id}`);
    }

    // Edit supplier / product on a pending purchase
    function editPurchase(row) {
        setEditRow(row);
    }

    // Print loader slip (purchase mode)
    function printLoaderSlip(row) {
        setLoaderSlipData({
            date: row.purchaseFirstStepDate || new Date().toISOString(),
            vehicleNumber: row.vehicleNumber || "",
            customerName: row.supplier?.name || "",
            productName: row.itemsList?.[0]?.inventoryItemName || "",
            emptyWeight: row.loadedWeight,
            correction: parseFloat(row.loadedWeightCorrection) || 0,
            operatorName: currentUser?.name || currentUser?.fullName || currentUser?.userName || "",
        });
        setShowLoaderSlip(true);
    }

    // Build columns
    const columns = useMemo(() => {
        return [
            {
                dataField: "vehicleNumber",
                text: "Vehicle #",
                search: true,
                formatter: (cell, row) => (
                    <>
                        <a href="#" onClick={(e) => { e.preventDefault(); completePurchase(row); }}>
                            {HeaderColumnFormatter(cell || 'N/A', row)}
                        </a>
                        {/* QR drafts live on Draft Purchases; a manual row the scanner flagged
                            (e.g. returned without unloading) still shows its Needs-review badge */}
                        <QrPurchaseBadges row={row} />
                    </>
                ),
                headerStyle: { width: '180px' }
            },
            {
                dataField: "supplier.name",
                text: "Supplier",
                search: true,
                formatter: (cell, row) => row.supplier?.name || '-'
            },
            {
                dataField: "itemsList",
                text: "Product",
                formatter: (cell) => {
                    if (!cell || cell.length === 0) return '-';
                    // Show first item name
                    return cell[0]?.inventoryItemName || '-';
                },
            },
            {
                dataField: "loadedWeight",
                text: "Loaded Weight",
                formatter: (cell) => cell ? `${cell} Ton` : '-',
                headerStyle: { width: '120px' }
            },
            {
                dataField: "purchaseFirstStepDate",
                text: "Entry Date",
                formatter: (cell) => formatDateTime(cell),
                headerStyle: { width: '180px' }
            },
            {
                dataField: "action",
                text: "Actions",
                headerStyle: { width: "240px" },
                formatter: ActionsColumnFormatter,
                formatExtraData: {
                    buttons: [
                        {
                            name: "Edit Supplier / Product",
                            color: "primary",
                            icon: "pencil",
                            method: editPurchase,
                        },
                        {
                            name: "Print Loader Slip",
                            color: "info",
                            icon: "print",
                            method: printLoaderSlip,
                        },
                        {
                            name: "Complete Purchase",
                            label: "Complete",
                            color: "success",
                            method: completePurchase,
                        },
                        {
                            name: "Delete",
                            color: "danger",
                            icon: "trash",
                            method: deleteRecord,
                            condition: isOlderThan48Hours,
                        },
                    ],
                },
                classes: "text-right pr-0",
                headerClasses: "text-right pr-3",
            },
        ];
    }, []);

    // Add new entry button
    function addButtonClicked() {
        navigate('/purchase-step1');
    }

    useEffect(() => {
        if (myData && myData.setBaseData) {
            myData.setBaseData({
                toolBarButtons: [
                    {
                        name: "Record 2 Step Purchase",
                        color: "primary",
                        icon: "fa fa-plus",
                        onClick: addButtonClicked,
                    },
                ]
            });
        }

        setPageLoadComplete(true);
    }, []);

    const BaseCard = () => (
        <div className="card card-body">
            {/* Info Banner */}
            <div className="alert alert-primary d-flex align-items-center mb-4" role="alert">
                <i className="bi bi-clock-history me-3 fs-4"></i>
                <div>
                    <strong>Pending Purchases</strong> — Loaded trucks awaiting completion.
                    <br />
                    <small>Click "Complete" to weigh empty and finish the purchase entry. Records older than 48 hours can be deleted.</small>
                </div>
            </div>

            {pageLoadComplete && (
                <GenericTable
                    reload={reload}
                    defaultData={defaultList}
                    columns={columns}
                    entityId={ENTITY_ID_RAW_PURCHASE}
                    fixedFilterColumn="isPartialRecord"
                    fixedFilterData={true}
                    extraFilters={NOT_QR_DRAFT_FILTERS}
                    sortBy="purchaseFirstStepDate"
                    sortOrder="desc"
                />
            )}
        </div>
    );

    return (
        <>
            <PageTitle breadcrumbs={profileBreadCrumbs}>Pending Purchases</PageTitle>
            <BaseCard />
            <LoaderSlip
                show={showLoaderSlip}
                slipData={loaderSlipData}
                mode="PURCHASE"
                onClose={() => setShowLoaderSlip(false)}
            />
            <PendingEntryEditDialog
                show={!!editRow}
                row={editRow}
                entityId={ENTITY_ID_RAW_PURCHASE}
                partyField="supplier"
                partyEntityId={ENTITY_ID_SUPPLIER}
                partyLabel="Supplier"
                onClose={() => setEditRow(null)}
                onSaved={() => setReload(prev => prev + 1)}
            />
        </>
    );
}
